import { useMemo, useState } from 'react'
import { useCart } from '@shopify/hydrogen-react'
import { useAppStore } from 'lib/hooks/useAppStore'
import { urlForImage } from 'lib/sanity.image'
import { getUrlFromLink } from 'lib/helpers'

import Image from 'next/image'
import Link from 'next/link'
import { motion, AnimatePresence } from 'motion/react'
import { Button } from 'components/Global/Button'
import { MobileCategoryItem } from 'components/Header/MobileCategoryItem'
import { ResponsiveImage } from 'components/Global/ResponsiveImage'
import { MobileSearchOverlay } from 'components/Global/Search/MobileSearchOverlay'
import { MobileSearchButton } from 'components/Global/Search/MobileSearchButton'
import { MenuIcon } from 'components/Icons/MenuIcon'
import { CloseIcon } from 'components/Global/CloseIcon'

const MenuButton = ({ children, onClick, className = '', ...rest }) => {
  return (
    <Button onClick={onClick} className={`${className} bg-white`} {...rest}>
      {children}
    </Button>
  )
}

const MenuLink = ({ item, closeMenu }) => {
  const href = getUrlFromLink(item)
  return (
    <li>
      <MenuButton href={href} onClick={closeMenu}>
        {item.label}
      </MenuButton>
    </li>
  )
}

const ExpandableMenu = ({ item, closeMenu }) => {
  const [expanded, setExpanded] = useState<string | null>()
  return item.links.map((link) => {
    switch (link._type) {
      case 'link':
        return <MenuLink key={link._key} item={link} closeMenu={closeMenu} />
      case 'dropdown':
        return (
          <li key={link._key}>
            <MenuButton onClick={() => setExpanded(link.label)}>
              {link.label}
            </MenuButton>
            <AnimatePresence mode="wait">
              {link.label === 'Causes' && expanded === 'Causes' ? (
                <CausesOverlay
                  key={link.label}
                  back={() => setExpanded(null)}
                  closeMenu={closeMenu}
                  links={link.links}
                />
              ) : null}
              {link.label !== 'Causes' && expanded === link.label ? (
                <CategoriesOverlay
                  key={link.label}
                  back={() => setExpanded(null)}
                  closeMenu={closeMenu}
                  links={link.links}
                />
              ) : null}
            </AnimatePresence>
          </li>
        )
      default:
        return null
    }
  })
}

const CausesOverlay = ({ links, closeMenu, back }) => {
  return (
    <motion.div
      className="h-screen-dynamic fixed inset-0 z-[10] w-full overflow-y-scroll overscroll-contain bg-[#E6E6E6] p-20 pt-100"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{
        duration: 0.3,
        ease: 'linear',
      }}
    >
      <div className="fixed left-0 top-0 z-50 flex w-full justify-between p-20">
        <MenuButton onClick={back} className="px-16 py-10 shadow-md">
          Back
        </MenuButton>
        <MenuButton onClick={closeMenu} className="px-14 py-12 shadow-md">
          <CloseIcon />
        </MenuButton>
      </div>
      <ul className="flex flex-col gap-8">
        {links.map((link) => {
          return (
            <li key={link._key} className="rounded-8 bg-white p-8">
              <Link
                scroll={false}
                href={
                  link.to.store
                    ? `/${link.to._type}s/${link.to.store.slug.current}`
                    : ''
                }
                className="flex gap-10"
                onClick={closeMenu}
              >
                <div className="relative aspect-square h-[50px] overflow-hidden rounded-6 bg-gray">
                  {link.to?.featuredImage ? (
                    <ResponsiveImage
                      src={urlForImage(link.to.featuredImage)
                        .width(200)
                        .auto('format')
                        .url()}
                      alt={link.to.store?.title ?? ''}
                      position="top"
                    />
                  ) : null}
                </div>
                <div className="flex-1">
                  <div className="text-darkGray">{link.label}</div>
                  <div className="line-clamp-3 pt-4 text-gray">
                    {link.to?.description}
                  </div>
                </div>
              </Link>
            </li>
          )
        })}
      </ul>
    </motion.div>
  )
}

const CategoriesOverlay = ({ links, closeMenu, back }) => {
  return (
    <motion.div
      className="h-screen-dynamic fixed inset-0 z-[10] w-full overflow-y-scroll overscroll-contain bg-[#E6E6E6] p-20 pt-100"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{
        duration: 0.3,
        ease: 'linear',
      }}
    >
      <div className="fixed left-0 top-0 z-50 flex w-full justify-between p-20">
        <MenuButton onClick={back} className="px-16 py-10 shadow-md">
          Back
        </MenuButton>
        <MenuButton onClick={closeMenu} className="px-14 py-12 shadow-md">
          <CloseIcon />
        </MenuButton>
      </div>
      <ul className="flex flex-col gap-8 text-darkGray">
        {links.map((link) => {
          return (
            <MobileCategoryItem key={link._key} {...link} onClick={closeMenu} />
          )
        })}
      </ul>
    </motion.div>
  )
}

export const HeaderMobile = ({
  menu,
  mobileMenu,
  logo,
  withBanner = false,
}) => {
  const { menuOpen, setMenuOpen, setCartState, cartState } = useAppStore()
  const { totalQuantity } = useCart()

  const logoUrl = useMemo(() => {
    return logo
      ? urlForImage(logo).width(100).auto('format').url()
      : '/chnge_logo.png'
  }, [logo])

  const bgClass = useMemo(() => {
    return `transition-colors duration-300 ${
      menuOpen ? 'bg-white' : 'background-blur'
    }`
  }, [menuOpen])

  const closeMenu = () => {
    setMenuOpen(false)
  }

  return (
    <header
      className={`fixed ${
        withBanner ? 'top-[20px]' : 'top-0'
      } z-10 w-full p-20 text-11 leading-110 text-darkGray md:hidden`}
    >
      <div className="relative z-10 flex w-full justify-between">
        <button
          className={`relative flex items-center gap-[3px] rounded-6 ${bgClass}`}
          onClick={() => setMenuOpen(true)}
        >
          <div className="py-14 pl-12">
            <Image src={logoUrl} width={40} height={8} alt="Chnge Logo" />
          </div>
          <MenuIcon />
        </button>
        <div className="flex gap-[5px]">
          <MobileSearchOverlay className={`${bgClass} px-14 py-[13px]`} />
          {totalQuantity && totalQuantity > 0 ? (
            <Button
              onClick={() => setCartState('open')}
              className={`${bgClass} px-16 py-12`}
            >
              {totalQuantity.toString().padStart(2, '0')}
            </Button>
          ) : null}
        </div>
      </div>
      <AnimatePresence mode="wait">
        {menuOpen ? (
          <motion.div
            className="h-screen-dynamic fixed inset-0 z-[10] flex w-full flex-col justify-between gap-6 bg-[#E6E6E6] p-20 pt-100"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              duration: 0.3,
              ease: 'linear',
            }}
          >
            <div className="fixed left-0 top-0 z-10 flex w-full justify-between p-20">
              <Button
                className={`${bgClass} py-14`}
                onClick={() => setMenuOpen(false)}
              >
                <Image src={logoUrl} width={40} height={8} alt="Chnge Logo" />
              </Button>
              <Button
                onClick={() => setMenuOpen(false)}
                className={`${bgClass} px-14 py-14`}
              >
                <CloseIcon />
              </Button>
            </div>
            <ul className="flex flex-col gap-6">
              <MenuLink
                item={{
                  label: 'Home',
                  type: 'internal',
                  to: { _type: 'homePage' },
                }}
                closeMenu={closeMenu}
              />
              {menu?.map((item) => {
                switch (item._type) {
                  case 'expandableMenu':
                    return (
                      <ExpandableMenu
                        key={item._key}
                        closeMenu={closeMenu}
                        item={item}
                      />
                    )
                  case 'link':
                    return (
                      <MenuLink
                        key={item._key}
                        item={item}
                        closeMenu={closeMenu}
                      />
                    )
                  default:
                    return null
                }
              })}
              <MobileSearchButton closeMenu={closeMenu} />
            </ul>
            <ul className="flex flex-col gap-6">
              {mobileMenu.map((item) => {
                return (
                  <MenuLink key={item._key} item={item} closeMenu={closeMenu} />
                )
              })}
            </ul>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </header>
  )
}
